/**
 * Renaming a client or a blueprint.
 *
 * The name is the label and the slug is the handle, and only the name changes.
 * The slug is shown read-only beside the field and said plainly to be fixed, so
 * nobody expects a link or the permanent-deletion confirmation to follow the
 * new name. There is deliberately no control that edits or regenerates it.
 *
 * A refusal (a name already taken, including by something in the archive) is shown
 * as the API worded it: for an archived holder that sentence says what to do, and
 * a generic "name taken" would not.
 */
import { useState, type FormEvent } from 'react';
import { Dialog } from '../versions/Dialog';
import { FormError } from './ui';

export function RenameDialog({
  open,
  title,
  fieldLabel,
  currentName,
  slug,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  open: boolean;
  title: string;
  fieldLabel: string;
  currentName: string;
  slug: string;
  pending: boolean;
  error: string | null;
  onSubmit: (name: string) => void;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} title={title} onClose={onClose} dismissible={!pending}>
      {open ? (
        <RenameForm
          fieldLabel={fieldLabel}
          currentName={currentName}
          slug={slug}
          pending={pending}
          error={error}
          onSubmit={onSubmit}
          onClose={onClose}
        />
      ) : null}
    </Dialog>
  );
}

function RenameForm({
  fieldLabel,
  currentName,
  slug,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  fieldLabel: string;
  currentName: string;
  slug: string;
  pending: boolean;
  error: string | null;
  onSubmit: (name: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(currentName);
  const trimmed = name.trim();
  const unchanged = trimmed === currentName;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (pending || !trimmed || unchanged) return;
    onSubmit(trimmed);
  };

  return (
    <form className="bp-dialog-form" onSubmit={submit} noValidate>
      <label className="field">
        <span className="field-label">{fieldLabel}</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
          required
          autoComplete="off"
          autoFocus
        />
      </label>
      <div className="field">
        <span className="field-label" id="rename-slug-label">
          Slug (fixed)
        </span>
        <code className="adm-code adm-fixed-slug" aria-labelledby="rename-slug-label">
          {slug}
        </code>
        <span className="adm-help">
          The slug does not change when the name does. Links that use it keep working, and it stays
          the word typed to confirm a permanent deletion.
        </span>
      </div>
      <FormError>{error}</FormError>
      <div className="bp-dialog-actions">
        <button type="button" className="btn" onClick={onClose} disabled={pending}>
          Cancel
        </button>
        <button type="submit" className="btn primary" disabled={pending || !trimmed || unchanged}>
          {pending ? 'Saving…' : 'Rename'}
        </button>
      </div>
    </form>
  );
}
